import React, { useState, useMemo, useCallback } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Platform,
} from "react-native";
import { ActivityIndicator } from "react-native-paper";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTranslation } from "../../i18n/useTranslation";

/* ------------------------------------------------------------------ */
/*  Difficulty helpers                                                  */
/* ------------------------------------------------------------------ */
function normalizeDiff(d, dl) {
  // d = difficulty string, dl = difficulty_level number (1=easy, 2=medium, 3=hard)
  const s = String(d || "").toLowerCase();
  if (s.includes("easy")) return "easy";
  if (s.includes("medium")) return "medium";
  if (s.includes("difficult") || s.includes("hard")) return "hard";
  // Fallback to numeric difficulty_level
  const n = Number(dl);
  if (n === 1) return "easy";
  if (n === 2) return "medium";
  if (n === 3) return "hard";
  return "unknown";
}

// AI-generation difficulty choices: `value` is sent to the backend (English), `label` is the t() key.
const AI_DIFFICULTY_OPTIONS = [
  { value: "easy", label: "Easy" },
  { value: "mixed", label: "Mixed" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

function countByDifficulty(list) {
  const out = { easy: 0, medium: 0, hard: 0, unknown: 0 };
  for (const q of list || []) out[normalizeDiff(q.difficulty, q.difficulty_level)]++;
  return out;
}

/* question key normalizer - matches existing codebase */
const qKey = (id) => String(id ?? "");

/* Max height for the independently-scrollable question list panel */
const QUESTION_LIST_MAX_HEIGHT = 600;

/* ------------------------------------------------------------------ */
/*  QuestionCard                                                        */
/* ------------------------------------------------------------------ */
function QuestionCard({ question, isSelected, onToggle, isDarkMode }) {
  const c = getColors(isDarkMode);
  const { t } = useTranslation();
  const diff = normalizeDiff(question.difficulty, question.difficulty_level);
  const diffColors = DIFF_COLORS[diff] || DIFF_COLORS.unknown;
  const dc = isDarkMode ? diffColors.dark : diffColors.light;

  const typeLabel = getQuestionTypeLabel(question.question_type, t);
  const questionText =
    typeof question.question === "string"
      ? question.question
      : question.question?.text || question.question_text || "";

  return (
    <TouchableOpacity
      style={[
        styles.qCard,
        {
          backgroundColor: isSelected ? c.accentLight : c.cardBg,
          borderColor: isSelected ? c.accent : c.border,
        },
      ]}
      onPress={onToggle}
      activeOpacity={0.7}
    >
      {/* Checkbox */}
      <View style={styles.qCheck}>
        <MaterialCommunityIcons
          name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
          size={20}
          color={isSelected ? c.accent : c.textMuted}
        />
      </View>

      {/* Body */}
      <View style={styles.qBody}>
        <Text style={[styles.qType, { color: c.accent }]}>{typeLabel}</Text>
        <Text style={[styles.qText, { color: c.textPrimary }]} numberOfLines={3}>
          {questionText}
        </Text>

        {/* MCQ options */}
        {question.options && question.options.option1 && (
          <View style={styles.qOptions}>
            {["option1", "option2", "option3", "option4"]
              .filter((k) => question.options[k])
              .map((k) => {
                const opt = question.options[k];
                const optText = typeof opt === "string" ? opt : opt?.text || "";
                const correctAns = question.options.correct_answer || question.options.correct_ans;
                // correct_answer is typically "1","2","3","4" or the option key index
                const optIdx = k.replace("option", "");
                const isCorrect = String(correctAns) === optIdx;
                return (
                  <View
                    key={k}
                    style={[
                      styles.qOption,
                      {
                        backgroundColor: isCorrect ? c.greenLight : c.sidebarBg,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.qOptionText,
                        { color: isCorrect ? c.greenText : c.textSecondary },
                        isCorrect && { fontWeight: "600" },
                      ]}
                    >
                      {optText}
                    </Text>
                  </View>
                );
              })}
          </View>
        )}
      </View>

      {/* Difficulty badge - hide for AI or unknown */}
      {diff !== "unknown" && question.question_source !== "ai" && (
        <View style={[styles.diffBadge, { backgroundColor: dc.bg }]}>
          <View style={[styles.diffDot, { backgroundColor: dc.dot }]} />
          <Text style={[styles.diffLabel, { color: dc.text }]}>
            {getDifficultyLabel(diff, t)}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
}

/* ------------------------------------------------------------------ */
/*  TopicTree                                                           */
/* ------------------------------------------------------------------ */
function TopicTree({
  levels,
  gradeFilter,
  subjectFilter,
  selectedTopics,
  onToggleGrade,
  onToggleSubject,
  onToggleTopic,
  isDarkMode,
}) {
  const c = getColors(isDarkMode);
  const { t } = useTranslation();
  const [expandedGrades, setExpandedGrades] = useState(new Set());
  const [expandedSubjects, setExpandedSubjects] = useState(new Set());
  const [expandedChapters, setExpandedChapters] = useState(new Set());

  const toggleExpand = (set, setter, id) => {
    setter((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Show all grades in the tree — the tree is collapsible so no need to filter
  const filteredLevels = levels;

  return (
    <View>
      {filteredLevels.map((level) => {
        const levelExpanded = expandedGrades.has(level.id);
        const subjects = level.subjects || [];

        return (
          <View key={level.id} style={{ marginBottom: 2 }}>
            <TouchableOpacity
              style={[styles.treeToggle, { borderRadius: 6 }]}
              onPress={() => toggleExpand(expandedGrades, setExpandedGrades, level.id)}
              activeOpacity={0.7}
            >
              <MaterialCommunityIcons
                name={levelExpanded ? "chevron-down" : "chevron-right"}
                size={16}
                color={c.textMuted}
              />
              <Text style={[styles.treeToggleText, { color: c.textPrimary }]}>
                {level.name || t("Grade {grade}", { grade: level.grade ?? level.id })}
              </Text>
            </TouchableOpacity>

            {levelExpanded &&
              subjects.map((subject) => {
                const subjExpanded = expandedSubjects.has(subject.id);
                return (
                  <View key={subject.id} style={{ paddingLeft: 22 }}>
                    <TouchableOpacity
                      style={styles.treeToggle}
                      onPress={() =>
                        toggleExpand(expandedSubjects, setExpandedSubjects, subject.id)
                      }
                      activeOpacity={0.7}
                    >
                      <MaterialCommunityIcons
                        name={subjExpanded ? "chevron-down" : "chevron-right"}
                        size={16}
                        color={c.textMuted}
                      />
                      <Text style={[styles.treeToggleText, { color: c.textPrimary }]}>
                        {subject.name}
                      </Text>
                    </TouchableOpacity>

                    {subjExpanded &&
                      (subject.chapters || []).map((chapter) => {
                        const chExpanded = expandedChapters.has(chapter.id);
                        return (
                          <View key={chapter.id} style={{ paddingLeft: 22 }}>
                            <TouchableOpacity
                              style={styles.treeToggle}
                              onPress={() =>
                                toggleExpand(
                                  expandedChapters,
                                  setExpandedChapters,
                                  chapter.id
                                )
                              }
                              activeOpacity={0.7}
                            >
                              <MaterialCommunityIcons
                                name={chExpanded ? "chevron-down" : "chevron-right"}
                                size={14}
                                color={c.textMuted}
                              />
                              <Text
                                style={[
                                  styles.treeToggleText,
                                  { color: c.textPrimary, fontSize: 13 },
                                ]}
                              >
                                {chapter.name}
                              </Text>
                            </TouchableOpacity>

                            {chExpanded &&
                              (chapter.topics || []).map((topic) => {
                                const topicKey = `${chapter.id}-${topic.id}`;
                                const isChecked = selectedTopics.has(topicKey);
                                return (
                                  <TouchableOpacity
                                    key={topic.id}
                                    style={[styles.treeLeaf, isChecked && { backgroundColor: c.accentLight }]}
                                    onPress={() => onToggleTopic(chapter.id, topic.id)}
                                    activeOpacity={0.7}
                                  >
                                    <MaterialCommunityIcons
                                      name={
                                        isChecked
                                          ? "checkbox-marked"
                                          : "checkbox-blank-outline"
                                      }
                                      size={16}
                                      color={isChecked ? c.accent : c.textMuted}
                                    />
                                    <Text
                                      style={[
                                        styles.treeLeafText,
                                        { color: c.textSecondary },
                                      ]}
                                    >
                                      {topic.name}
                                    </Text>
                                  </TouchableOpacity>
                                );
                              })}
                          </View>
                        );
                      })}
                  </View>
                );
              })}
          </View>
        );
      })}
    </View>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                      */
/* ------------------------------------------------------------------ */
export default function WizardStep2Questions({
  isDarkMode,
  quizData,
  selectedQuestionIds,
  onToggleQuestion,
  onSelectAll,
  onClearAll,
  onFetchQuestions,
  onGenerateAI,
  onNext,
  onBack,
  // Curriculum tree props
  levels = [],
  gradeFilter = new Set(),
  subjectFilter = new Set(),
  selectedTopics = new Set(),
  onToggleGrade,
  onToggleSubject,
  onToggleTopic,
  // AI props
  aiState = {},
  onUpdateAI,
  loading = false,
}) {
  const c = getColors(isDarkMode);
  const { t } = useTranslation();
  const [activePath, setActivePath] = useState("bank");
  const [diffFilter, setDiffFilter] = useState(new Set(["easy", "medium", "hard"]));

  const toggleDiffFilter = useCallback(
    (d) => {
      setDiffFilter((prev) => {
        const next = new Set(prev);
        next.has(d) ? next.delete(d) : next.add(d);
        return next;
      });
    },
    []
  );

  const counts = useMemo(() => countByDifficulty(quizData), [quizData]);
  const filteredQuestions = useMemo(() => {
    if (!diffFilter.size) return quizData;
    return quizData.filter((q) => {
      const d = normalizeDiff(q.difficulty, q.difficulty_level);
      return d === "unknown" || diffFilter.has(d);
    });
  }, [quizData, diffFilter]);

  const selectedCount = selectedQuestionIds.size;

  return (
    <View>
      {/* Header */}
      <View style={styles.header}>
        <Text style={[styles.heading, { color: c.textPrimary }]}>{t("Add Questions")}</Text>
        <Text style={[styles.subheading, { color: c.textSecondary }]}>
          {t("Build your test from the question bank or generate new questions with AI.")}
        </Text>
      </View>

      {/* Path toggle */}
      <View style={[styles.pathToggle, { backgroundColor: c.sidebarBg }]}>
        <TouchableOpacity
          style={[
            styles.pathBtn,
            activePath === "bank" && [styles.pathBtnActive, { backgroundColor: c.cardBg }],
          ]}
          onPress={() => setActivePath("bank")}
          activeOpacity={0.7}
          title={t("Question Bank")}
        >
          <MaterialCommunityIcons
            name="database-outline"
            size={14}
            color={activePath === "bank" ? c.textPrimary : c.textMuted}
            style={{ marginRight: 4 }}
          />
          <Text
            style={[
              styles.pathBtnText,
              { color: activePath === "bank" ? c.textPrimary : c.textMuted },
            ]}
          >
            {t("Question Bank")}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.pathBtn,
            activePath === "ai" && [styles.pathBtnActive, { backgroundColor: c.cardBg }],
          ]}
          onPress={() => setActivePath("ai")}
          activeOpacity={0.7}
          title={t("AI Generate")}
        >
          <MaterialCommunityIcons
            name="lightbulb-on-outline"
            size={14}
            color={activePath === "ai" ? c.textPrimary : c.textMuted}
            style={{ marginRight: 4 }}
          />
          <Text
            style={[
              styles.pathBtnText,
              { color: activePath === "ai" ? c.textPrimary : c.textMuted },
            ]}
          >
            {t("AI Generate")}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Bank Path */}
      {activePath === "bank" && (
        <View style={styles.bankLayout}>
          {/* Sidebar - Topic Tree */}
          <View style={[styles.bankSidebar, { backgroundColor: c.cardBg, borderColor: c.border }]}>
            <Text style={[styles.sidebarTitle, { color: c.textMuted }]}>{t("SELECT TOPICS")}</Text>
            <ScrollView style={{ maxHeight: 500 }} nestedScrollEnabled>
              <TopicTree
                levels={levels}
                gradeFilter={gradeFilter}
                subjectFilter={subjectFilter}
                selectedTopics={selectedTopics}
                onToggleGrade={onToggleGrade}
                onToggleSubject={onToggleSubject}
                onToggleTopic={onToggleTopic}
                isDarkMode={isDarkMode}
              />
            </ScrollView>
            <TouchableOpacity
              style={[styles.fetchBtn, { backgroundColor: c.accent }]}
              onPress={onFetchQuestions}
              activeOpacity={0.7}
              disabled={loading}
              title={t("Fetch Questions")}
            >
              {loading ? (
                <ActivityIndicator size={20} color="#fff" />
              ) : (
                <>
                  <MaterialCommunityIcons name="magnify" size={14} color="#fff" />
                  <Text style={styles.fetchBtnText}>{t("Fetch Questions")}</Text>
                </>
              )}
            </TouchableOpacity>
          </View>

          {/* Main question list */}
          <View style={styles.bankMain}>
            {/* Count + difficulty filters */}
            <View style={styles.bankHeader}>
              <Text style={[styles.bankCount, { color: c.textPrimary }]}>
                <Text style={{ color: c.accent, fontWeight: "800" }}>{quizData.length}</Text>
                {" " + t("questions found") + "  \u00B7  "}
                <Text style={{ color: c.teal, fontWeight: "700" }}>{selectedCount} {t("selected")}</Text>
              </Text>
              <View style={styles.diffFilters}>
                {[
                  { key: "easy", label: `${t("Easy")} (${counts.easy})` },
                  { key: "medium", label: `${t("Medium")} (${counts.medium})` },
                  { key: "hard", label: `${t("Hard")} (${counts.hard})` },
                ].map((d) => {
                  const active = diffFilter.has(d.key);
                  const chipC = active
                    ? isDarkMode
                      ? DIFF_CHIP_DARK[d.key]
                      : DIFF_CHIP_LIGHT[d.key]
                    : { bg: "transparent", border: c.border, text: c.textSecondary };
                  return (
                    <TouchableOpacity
                      key={d.key}
                      style={[
                        styles.diffChip,
                        {
                          backgroundColor: chipC.bg,
                          borderColor: chipC.border,
                        },
                      ]}
                      onPress={() => toggleDiffFilter(d.key)}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.diffChipText, { color: chipC.text }]}>{d.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            {/* Select all / clear all */}
            {quizData.length > 0 && (
              <View style={styles.selectActions}>
                <TouchableOpacity onPress={() => onSelectAll(filteredQuestions)} activeOpacity={0.7} title={t("Select All Visible")}>
                  <Text style={[styles.selectActionText, { color: c.accent }]}>{t("Select All Visible")}</Text>
                </TouchableOpacity>
                <Text style={{ color: c.textMuted }}> | </Text>
                <TouchableOpacity onPress={onClearAll} activeOpacity={0.7} title={t("Clear All")}>
                  <Text style={[styles.selectActionText, { color: c.textMuted }]}>{t("Clear All")}</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Scrollable questions list */}
            {filteredQuestions.length > 0 ? (
              <ScrollView
                style={[styles.questionListScroll, { borderColor: c.border }]}
                contentContainerStyle={styles.questionListContent}
                nestedScrollEnabled
              >
                {filteredQuestions.map((q) => (
                  <QuestionCard
                    key={qKey(q.question_id)}
                    question={q}
                    isSelected={selectedQuestionIds.has(qKey(q.question_id))}
                    onToggle={() => onToggleQuestion(q.question_id)}
                    isDarkMode={isDarkMode}
                  />
                ))}
              </ScrollView>
            ) : (
              !loading && (
                <View style={styles.emptyState}>
                  <MaterialCommunityIcons name="file-search-outline" size={48} color={c.textMuted} />
                  <Text style={[styles.emptyText, { color: c.textMuted }]}>
                    {t("Select topics and click \"Fetch Questions\" to load questions from the bank.")}
                  </Text>
                </View>
              )
            )}
          </View>
        </View>
      )}

      {/* AI Path */}
      {activePath === "ai" && (
        <View style={styles.bankLayout}>
          {/* Left: Topic tree (shared with bank path) */}
          <View style={[styles.bankSidebar, { backgroundColor: c.sidebarBg, borderColor: c.border }]}>
            <Text style={[styles.sidebarTitle, { color: c.textSecondary }]}>{t("SELECT TOPICS")}</Text>
            <ScrollView style={{ flex: 1 }}>
              <TopicTree
                levels={levels}
                selectedTopics={selectedTopics}
                onToggleTopic={onToggleTopic}
                isDarkMode={isDarkMode}
              />
            </ScrollView>
            {selectedTopics.size > 0 && (
              <View style={{ paddingTop: 8 }}>
                <Text style={{ color: c.accent, fontSize: 12, fontWeight: "600" }}>
                  {selectedTopics.size === 1
                    ? t("{count} topic selected", { count: selectedTopics.size })
                    : t("{count} topics selected", { count: selectedTopics.size })}
                </Text>
              </View>
            )}
          </View>

          {/* Right: AI settings */}
          <View style={{ flex: 1 }}>
            <View style={[styles.aiPanel, { backgroundColor: c.cardBg, borderColor: c.border }]}>
              <Text style={[styles.aiTitle, { color: c.textPrimary }]}>{t("AI Question Generator")}</Text>
              <Text style={[styles.aiDesc, { color: c.textSecondary }]}>
                {t("Select topics from the tree, then configure question types. AI will generate assessment-ready questions scoped to your selected curriculum topics.")}
              </Text>

              {/* Grade + Difficulty row */}
              <View style={[styles.formRow, { marginBottom: 16 }]}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.label, { color: c.textSecondary }]}>{t("Grade Level")}</Text>
                  <TextInput
                    style={[styles.input, { color: c.textPrimary, backgroundColor: c.inputBg, borderColor: c.border }]}
                    placeholder={t("e.g. 10")}
                    placeholderTextColor={c.textMuted}
                    value={String(aiState.grade || "")}
                    onChangeText={(v) => onUpdateAI("grade", v)}
                    keyboardType="numeric"
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.label, { color: c.textSecondary }]}>{t("Difficulty")}</Text>
                  {/* Values stay English: the backend prompt and normalizeDiff read them. */}
                  <View style={styles.diffFilters}>
                    {AI_DIFFICULTY_OPTIONS.map((opt) => {
                      const active = (aiState.difficulty || "mixed") === opt.value;
                      return (
                        <TouchableOpacity
                          key={opt.value}
                          style={[
                            styles.diffChip,
                            {
                              backgroundColor: active ? c.accent : "transparent",
                              borderColor: active ? c.accent : c.border,
                            },
                          ]}
                          onPress={() => onUpdateAI("difficulty", opt.value)}
                          activeOpacity={0.7}
                          accessibilityRole="button"
                          accessibilityState={{ selected: active }}
                        >
                          <Text
                            style={[
                              styles.diffChipText,
                              { color: active ? "#fff" : c.textSecondary },
                            ]}
                          >
                            {t(opt.label)}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </View>
              </View>

              {/* Instructions */}
              <View style={{ marginBottom: 20 }}>
                <Text style={[styles.label, { color: c.textSecondary }]}>{t("Additional Instructions (optional)")}</Text>
                <TextInput
                  style={[
                    styles.input,
                    styles.textarea,
                    { color: c.textPrimary, backgroundColor: c.inputBg, borderColor: c.border },
                  ]}
                  placeholder={t("e.g., Focus on membrane transport mechanisms, include diagram-based questions...")}
                  placeholderTextColor={c.textMuted}
                  value={aiState.instructions || ""}
                  onChangeText={(v) => onUpdateAI("instructions", v)}
                  multiline
                  numberOfLines={3}
                />
              </View>

              {/* Question type counts */}
              <View style={styles.aiCounts}>
                {[
                  { key: "mcq", label: t("MCQ") },
                  { key: "fill", label: t("Fill-in-Blank") },
                  { key: "shortEssay", label: t("Short Essay") },
                  { key: "longEssay", label: t("Long Essay") },
                  { key: "assoc", label: t("Word Assoc.") },
                ].map((item) => (
                  <View
                    key={item.key}
                    style={[styles.aiCountBox, { borderColor: c.border }]}
                  >
                    <Text style={[styles.aiCountLabel, { color: c.textMuted }]}>{item.label}</Text>
                    <TextInput
                      style={[
                        styles.aiCountInput,
                        { color: c.textPrimary, backgroundColor: c.inputBg, borderColor: c.border },
                      ]}
                      value={String(aiState[item.key] || "0")}
                      onChangeText={(v) => onUpdateAI(item.key, v)}
                      keyboardType="numeric"
                    />
                  </View>
                ))}
              </View>

              {/* Generate button */}
              {loading ? (
                <View style={[styles.generateBtn, { backgroundColor: c.accent }]}>
                  <ActivityIndicator size={20} color="#fff" />
                  <Text style={styles.generateBtnText}>{t("Generating... this may take a minute")}</Text>
                </View>
              ) : (
                <TouchableOpacity
                  style={[
                    styles.generateBtn,
                    { backgroundColor: selectedTopics.size > 0 ? c.accent : c.textMuted },
                  ]}
                  onPress={onGenerateAI}
                  activeOpacity={0.7}
                  disabled={selectedTopics.size === 0}
                  title={t("Generate questions")}
                >
                  <MaterialCommunityIcons name="lightbulb-on-outline" size={16} color="#fff" />
                  <Text style={styles.generateBtnText}>
                    {t("Generate")}{" "}
                    {getTotalAICount(aiState)} {t("Questions")}
                  </Text>
                </TouchableOpacity>
              )}
              {selectedTopics.size === 0 && (
                <Text style={{ color: c.textMuted, fontSize: 12, marginTop: 8, textAlign: "center" }}>
                  {t("Select at least one topic from the tree to generate questions")}
                </Text>
              )}
            </View>

            {/* Show generated questions for review/selection */}
            {quizData.length > 0 && (
              <View style={{ marginTop: 24 }}>
                <View style={styles.resultsHeader}>
                  <Text style={[styles.resultsCount, { color: c.textPrimary }]}>
                    <Text style={{ color: c.accent, fontWeight: "700" }}>{quizData.length}</Text>
                    {" "}{t("questions generated")}  {"\u00B7"}  <Text style={{ color: c.teal, fontWeight: "700" }}>{selectedQuestionIds.size}</Text> {t("selected")}
                  </Text>
                  <View style={{ flexDirection: "row", gap: 8 }}>
                    <TouchableOpacity onPress={() => onSelectAll(quizData)} title={t("Select All")}>
                      <Text style={{ color: c.accent, fontSize: 13, fontWeight: "600" }}>{t("Select All")}</Text>
                    </TouchableOpacity>
                    <Text style={{ color: c.textMuted }}>|</Text>
                    <TouchableOpacity onPress={onClearAll} title={t("Clear All")}>
                      <Text style={{ color: c.textMuted, fontSize: 13 }}>{t("Clear All")}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
                {/* Scrollable AI-generated questions list */}
                <ScrollView
                  style={[styles.questionListScroll, { borderColor: c.border }]}
                  contentContainerStyle={styles.questionListContent}
                  nestedScrollEnabled
                >
                  {quizData.map((q) => (
                    <QuestionCard
                      key={q.question_id}
                      question={q}
                      isSelected={selectedQuestionIds.has(qKey(q.question_id))}
                      onToggle={() => onToggleQuestion(qKey(q.question_id))}
                      isDarkMode={isDarkMode}
                    />
                  ))}
                </ScrollView>
              </View>
            )}
          </View>
        </View>
      )}

      {/* Step Actions */}
      <View style={[styles.stepActions, { borderTopColor: c.border }]}>
        <TouchableOpacity
          style={[styles.btnSecondary, { borderColor: c.border, backgroundColor: c.cardBg }]}
          onPress={onBack}
          activeOpacity={0.7}
          title={t("Back")}
        >
          <MaterialCommunityIcons name="chevron-left" size={16} color={c.textPrimary} />
          <Text style={[styles.btnSecondaryText, { color: c.textPrimary }]}>{t("Back")}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btnPrimary, { backgroundColor: c.accent }]}
          onPress={onNext}
          activeOpacity={0.7}
          title={t("Review Selected")}
        >
          <Text style={styles.btnPrimaryText}>{t("Review Selected")} ({selectedCount})</Text>
          <MaterialCommunityIcons name="chevron-right" size={16} color="#fff" />
        </TouchableOpacity>
      </View>
    </View>
  );
}

function getTotalAICount(aiState) {
  return (
    (parseInt(aiState.mcq, 10) || 0) +
    (parseInt(aiState.fill, 10) || 0) +
    (parseInt(aiState.shortEssay, 10) || 0) +
    (parseInt(aiState.longEssay, 10) || 0) +
    (parseInt(aiState.assoc, 10) || 0)
  );
}

function getDifficultyLabel(diff, t) {
  if (diff === "easy") return t("Easy");
  if (diff === "medium") return t("Medium");
  return t("Hard");
}

function getQuestionTypeLabel(qt, t) {
  // Numeric codes from curriculum DB: 1=Fill in blank, 2=MCQ, 3=Fill in blanks, 4=True/False, 7=Paragraph
  if (typeof qt === "number" || (typeof qt === "string" && /^\d+$/.test(qt))) {
    const byCode = {
      1: t("Fill in the Blank"),
      2: t("Multiple Choice"),
      3: t("Fill in the Blank"),
      4: t("True / False"),
      7: t("Paragraph"),
      8: t("Paragraph"),
    };
    return byCode[Number(qt)] || t("Question");
  }
  const s = String(qt || "").toLowerCase();
  if (s.includes("mcq") || s.includes("multiple")) return t("Multiple Choice");
  if (s.includes("fill")) return t("Fill in the Blank");
  if (s.includes("true") || s.includes("tf")) return t("True / False");
  if (s.includes("short")) return t("Short Essay");
  if (s.includes("long")) return t("Long Essay");
  if (s.includes("assoc") || s.includes("word")) return t("Word Association");
  return s || t("Question");
}

/* ------------------------------------------------------------------ */
/*  Colors / theme                                                      */
/* ------------------------------------------------------------------ */
function getColors(isDarkMode) {
  if (isDarkMode) {
    return {
      textPrimary: "#f1f5f9",
      textSecondary: "#94a3b8",
      textMuted: "#64748b",
      border: "#334155",
      cardBg: "#1e293b",
      sidebarBg: "#162032",
      inputBg: "#1e293b",
      accent: "#818cf8",
      accentLight: "rgba(99,102,241,0.12)",
      teal: "#2dd4bf",
      greenLight: "rgba(22,163,74,0.12)",
      greenText: "#86efac",
      red: "#f87171",
    };
  }
  return {
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#94a3b8",
    border: "#e2e8f0",
    cardBg: "#ffffff",
    sidebarBg: "#f1f5f9",
    inputBg: "#f8fafc",
    accent: "#6366f1",
    accentLight: "#eef2ff",
    teal: "#0d9488",
    greenLight: "#f0fdf4",
    greenText: "#166534",
    red: "#dc2626",
  };
}

const DIFF_COLORS = {
  easy: {
    light: { bg: "#dcfce7", text: "#166534", dot: "#16a34a" },
    dark: { bg: "rgba(34,197,94,0.15)", text: "#86efac", dot: "#4ade80" },
  },
  medium: {
    light: { bg: "#fef9c3", text: "#854d0e", dot: "#d97706" },
    dark: { bg: "rgba(234,179,8,0.15)", text: "#fde047", dot: "#fbbf24" },
  },
  hard: {
    light: { bg: "#fee2e2", text: "#991b1b", dot: "#dc2626" },
    dark: { bg: "rgba(239,68,68,0.15)", text: "#fca5a5", dot: "#f87171" },
  },
  unknown: {
    light: { bg: "#f1f5f9", text: "#64748b", dot: "#94a3b8" },
    dark: { bg: "#1e293b", text: "#64748b", dot: "#64748b" },
  },
};

const DIFF_CHIP_LIGHT = {
  easy: { bg: "#dcfce7", border: "#86efac", text: "#166534" },
  medium: { bg: "#fef9c3", border: "#fde047", text: "#854d0e" },
  hard: { bg: "#fee2e2", border: "#fca5a5", text: "#991b1b" },
};

const DIFF_CHIP_DARK = {
  easy: { bg: "rgba(34,197,94,0.15)", border: "rgba(34,197,94,0.4)", text: "#86efac" },
  medium: { bg: "rgba(234,179,8,0.15)", border: "rgba(234,179,8,0.4)", text: "#fde047" },
  hard: { bg: "rgba(239,68,68,0.15)", border: "rgba(239,68,68,0.4)", text: "#fca5a5" },
};

/* ------------------------------------------------------------------ */
/*  Styles                                                              */
/* ------------------------------------------------------------------ */
const styles = StyleSheet.create({
  header: { marginBottom: 24 },
  heading: { fontSize: 24, fontWeight: "400", marginBottom: 6 },
  subheading: { fontSize: 14, lineHeight: 21 },

  /* Path toggle */
  pathToggle: {
    flexDirection: "row",
    gap: 4,
    borderRadius: 10,
    padding: 4,
    marginBottom: 20,
  },
  pathBtn: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 10,
    borderRadius: 6,
  },
  pathBtnActive: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 2,
    elevation: 1,
  },
  pathBtnText: { fontSize: 13, fontWeight: "600" },

  /* Bank layout */
  bankLayout: { flexDirection: "row", gap: 24 },
  bankSidebar: {
    width: 280,
    borderWidth: 1,
    borderRadius: 16,
    padding: 18,
  },
  sidebarTitle: {
    fontSize: 13,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginBottom: 14,
  },
  bankMain: { flex: 1, minWidth: 0 },
  bankHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 14,
    flexWrap: "wrap",
    gap: 8,
  },
  bankCount: { fontSize: 14, fontWeight: "600" },

  /* Difficulty filter chips */
  diffFilters: { flexDirection: "row", gap: 8, flexWrap: "wrap" },
  diffChip: {
    paddingVertical: 5,
    paddingHorizontal: 14,
    borderRadius: 20,
    borderWidth: 1,
  },
  diffChipText: { fontSize: 12, fontWeight: "600" },

  /* Select actions */
  selectActions: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  selectActionText: { fontSize: 12, fontWeight: "600" },

  /* Question list container — scrolling handled by parent wizardMain */
  questionListScroll: {
    borderRadius: 12,
  },
  questionListContent: {
    paddingBottom: 8,
  },

  /* Question cards */
  qCard: {
    flexDirection: "row",
    gap: 14,
    padding: 16,
    borderWidth: 1,
    borderRadius: 10,
    marginBottom: 10,
    overflow: "hidden",
  },
  qCheck: { flexShrink: 0, marginTop: 2 },
  qBody: { flex: 1, minWidth: 0 },
  qType: {
    fontSize: 11,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  qText: { fontSize: 14, lineHeight: 21, marginBottom: 8 },
  qOptions: { flexDirection: "row", flexWrap: "wrap", gap: 6, maxWidth: "100%" },
  qOption: { paddingVertical: 4, paddingHorizontal: 10, borderRadius: 6, flexShrink: 1, maxWidth: "100%" },
  qOptionText: { fontSize: 12, flexShrink: 1 },
  diffBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 4,
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 20,
  },
  diffDot: { width: 5, height: 5, borderRadius: 2.5 },
  diffLabel: { fontSize: 10, fontWeight: "700" },

  /* Topic tree */
  treeToggle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  treeToggleText: { fontSize: 13, fontWeight: "600" },
  treeLeaf: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    paddingLeft: 32,
    borderRadius: 6,
  },
  treeLeafText: { fontSize: 13 },

  fetchBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 10,
    marginTop: 16,
  },
  fetchBtnText: { color: "#fff", fontSize: 12, fontWeight: "600" },

  /* Empty state */
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 60,
    gap: 12,
  },
  emptyText: { fontSize: 14, textAlign: "center", maxWidth: 300 },

  /* AI panel */
  aiPanel: {
    borderWidth: 1,
    borderRadius: 16,
    padding: 24,
  },
  aiTitle: { fontSize: 20, fontWeight: "700", marginBottom: 4 },
  aiDesc: { fontSize: 13, marginBottom: 20, lineHeight: 19 },
  formRow: { flexDirection: "row", gap: 16 },
  label: { fontSize: 13, fontWeight: "600", marginBottom: 6, letterSpacing: 0.2 },
  input: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderRadius: 6,
    fontSize: 14,
  },
  textarea: { minHeight: 80, textAlignVertical: "top" },
  aiCounts: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 20,
  },
  aiCountBox: {
    width: "30%",
    minWidth: 140,
    padding: 14,
    borderWidth: 1,
    borderRadius: 10,
    alignItems: "center",
  },
  aiCountLabel: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 6,
  },
  aiCountInput: {
    width: 60,
    textAlign: "center",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderRadius: 6,
    fontSize: 16,
    fontWeight: "700",
  },
  generateBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 14,
    borderRadius: 10,
  },
  generateBtnText: { color: "#fff", fontSize: 14, fontWeight: "600" },

  /* AI results */
  resultsHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  resultsCount: { fontSize: 14, fontWeight: "600" },

  /* Step actions */
  stepActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 32,
    paddingTop: 24,
    borderTopWidth: 1,
  },
  btnPrimary: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 22,
    borderRadius: 10,
    shadowColor: "rgba(99,102,241,0.25)",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 3,
  },
  btnPrimaryText: { color: "#fff", fontSize: 14, fontWeight: "600" },
  btnSecondary: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 22,
    borderRadius: 10,
    borderWidth: 1,
  },
  btnSecondaryText: { fontSize: 14, fontWeight: "600" },
});
